import { NULL_TAG } from '@racemap/utilities/consts/events';
import { buildAvailableTagsWithCount } from '@racemap/utilities/functions/starterTags/buildAvailableTagsWithCount';
import { getVisibleStarterGroups } from './parse';
import type {
  EventState,
  ExternalLayerType,
  PoiType,
  StarterType,
  State,
  TimerState,
  TrackType,
} from './store';
import {
  intersection,
  matchesTagSelection,
  memoizeSelector,
  singleMemoize,
  singleMemoize2,
} from './utils';

export const getCurrentEvents = memoizeSelector(
  (state: State) => state.currentEventIndices.map((i) => state.events[i]),
  (state: State) => state.currentEventIndices.map((i) => state.events[i]),
);

export function reduceMin(
  events: Array<EventState>,
  selector: (arg0: EventState) => number,
): number {
  return events.reduce((r, a) => Math.min(r, selector(a)), Number.POSITIVE_INFINITY);
}
export function reduceMax(
  events: Array<EventState>,
  selector: (arg0: EventState) => number,
): number {
  return events.reduce((r, a) => Math.max(r, selector(a)), Number.NEGATIVE_INFINITY);
}
export function reduceStartTime(events: Array<EventState>): number {
  return reduceMin(events, (a) => a.startTime);
}
export function reduceEndTime(events: Array<EventState>): number {
  return reduceMax(events, (a) => a.endTime);
}
export function reduceAny<T>(
  events: Array<EventState>,
  selector: (arg0: EventState) => T | null,
): T | null {
  const item = events.find((a) => selector(a) != null);
  return item != null ? selector(item) : null;
}

export const getEventIds = singleMemoize<Array<EventState>, Array<string>>(
  (events: Array<EventState>) => events.map((ev) => ev.eventId),
);
export const getEventStarters = singleMemoize<Array<EventState>, Array<StarterType>>(
  (events: Array<EventState>) =>
    events.reduce((r, ev) => r.concat(ev.starters), [] as Array<StarterType>),
);
export const getVisibleEventStarters = singleMemoize2<
  Array<EventState>,
  Set<string>,
  Array<StarterType>
>((events: Array<EventState>, hiddenIds: Set<string>) =>
  events.reduce(
    (r, ev) => r.concat(ev.starters.filter((starter) => !hiddenIds.has(starter.id))),
    [] as Array<StarterType>,
  ),
);
export const getVisibleEventPois = singleMemoize2<Array<EventState>, Set<string>, Array<PoiType>>(
  (events: Array<EventState>, hiddenIds: Set<string>) =>
    events.reduce(
      (r, ev) => r.concat(ev.pois.filter((poi) => !hiddenIds.has(poi.id))),
      [] as Array<PoiType>,
    ),
);
export const getVisibleEventTracks = singleMemoize2<
  Array<EventState>,
  Set<string>,
  Array<TrackType>
>((events: Array<EventState>, hiddenIds: Set<string>) =>
  events.reduce(
    (r, ev) => r.concat(ev.tracks.filter((track) => !hiddenIds.has(track.id))),
    [] as Array<TrackType>,
  ),
);
export const getEventExternalLayers = singleMemoize<Array<EventState>, Array<ExternalLayerType>>(
  (events: Array<EventState>) =>
    events.reduce((r, ev) => r.concat(ev.externalLayers), [] as Array<ExternalLayerType>),
);
export function getVisibleSelectedStarters(state: State): Array<StarterType> {
  return state.timer.selected
    .flatMap((starterId) => state.startersById.get(starterId) ?? [])
    .filter((starter) => !state.timer.hidden.has(starter.id));
}
export function getVisibleSelectedPois(state: State): Array<PoiType> {
  return state.timer.selected.flatMap((poiId) =>
    state.events.flatMap((ev) =>
      ev.pois.filter((poi) => poi.id === poiId && !state.timer.hidden.has(poi.id)),
    ),
  );
}
export function getVisibleSelectedTracks(state: State): Array<TrackType> {
  return state.timer.selected.flatMap((trackId) =>
    state.events.flatMap((ev) =>
      ev.tracks.filter((track) => track.id === trackId && !state.timer.hidden.has(track.id)),
    ),
  );
}
export function getCurrentSelectedIds(state: State): Array<string> {
  const currentEvents = getCurrentEvents(state);
  return state.timer.selected.filter((selectedId) =>
    currentEvents.some(
      (ev) =>
        ev.starters.some((s) => s.id === selectedId) ||
        ev.pois.some((p) => p.id === selectedId) ||
        ev.tracks.some((t) => t.id === selectedId),
    ),
  );
}

export const getSelectedIds = memoizeSelector(
  (timerState: TimerState): Set<string> => {
    const set = new Set<string>();
    if (timerState.highlighted != null) {
      set.add(timerState.highlighted);
    }
    for (const id of timerState.selected) {
      set.add(id);
    }
    return set;
  },
  (timerState: TimerState) => [timerState.highlighted, ...timerState.selected],
);

export const getHiddenIds = memoizeSelector(
  (timerState: TimerState): Set<string> => timerState.hidden,
  (timerState: TimerState) => [timerState.hidden],
);

function getTagKeysOfAllStarters(state: State): Set<string> {
  const tagKeys: Set<string> = new Set();
  for (const event of state.currentEventIndices.map((i) => state.events[i])) {
    for (const starter of event.starters) {
      for (const key of starter.tags.keys()) {
        tagKeys.add(key);
      }
    }
  }
  return tagKeys;
}

export const getCurrentAvailableTags = memoizeSelector(
  (state: State) => {
    const starters = state.currentEventIndices.flatMap((i) => state.events[i].starters);
    const tagCategoriesConfig =
      state.events[state.currentEventIndices[0]]?.options.tagCategoriesConfig;
    if (tagCategoriesConfig == null) return new Map();

    return buildAvailableTagsWithCount(starters, tagCategoriesConfig);
  },
  (state: State) => state.currentEventIndices.map((i) => state.events[i]),
);

export const getCurrentAvailableStarterGroups = memoizeSelector(
  (state: State) => {
    const visibleStarterGroups = new Set(
      state.currentEventIndices.flatMap((i) =>
        getVisibleStarterGroups(state.events[i].options.tagCategoriesConfig),
      ),
    );
    const tagKeys = intersection(getTagKeysOfAllStarters(state), visibleStarterGroups);

    const outputMap: Map<string, Map<string, number>> = new Map(
      Array.from(tagKeys, (tagKey) => [tagKey, new Map()]),
    );
    for (const event of state.currentEventIndices.map((i) => state.events[i])) {
      for (const starter of event.starters) {
        if (matchesTagSelection(starter.tags, state.timer.selectedTags)) {
          for (const tagKey of tagKeys) {
            const valueMap = outputMap.get(tagKey);
            if (valueMap == null) continue;

            const value = starter.tags.get(tagKey) ?? NULL_TAG;
            valueMap.set(value, (valueMap.get(value) ?? 0) + 1);
          }
        }
      }
    }
    const output: Map<string, Array<[string, number]>> = new Map(
      Array.from(outputMap, ([key, values]) => [
        key,
        Array.from(values).sort(([valueA], [valueB]) => valueA.localeCompare(valueB)),
      ]),
    );
    return output;
  },
  (state: State) => [
    state.timer.selectedTags,
    ...state.currentEventIndices.map((i) => state.events[i]),
  ],
);

export const getCurrentAvailableSelectableTags = memoizeSelector(
  (state: State) => {
    const tagKeys = getTagKeysOfAllStarters(state);
    const selectedTags = state.timer.selectedTags;
    const outputMap: Map<string, Map<string, number>> = new Map(
      Array.from(tagKeys, (tagKey) => [tagKey, new Map()]),
    );
    for (const event of state.currentEventIndices.map((i) => state.events[i])) {
      for (const starter of event.starters) {
        for (const tagKey of tagKeys) {
          const isSelected = Array.from(selectedTags)
            .filter(([k]) => k !== tagKey)
            .every(
              ([key, values]) =>
                (starter.tags.has(key) && values.includes(starter.tags.get(key) || '')) ||
                values.includes(NULL_TAG),
            );
          const valueMap = outputMap.get(tagKey);
          if (valueMap == null) continue;

          const value = starter.tags.get(tagKey) ?? NULL_TAG;
          const count = (valueMap.get(value) ?? 0) + (isSelected ? 1 : 0);
          if (count > 0) {
            valueMap.set(value, count);
          } else {
            valueMap.delete(value);
          }
        }
      }
    }
    const output: Map<string, Array<[string, number]>> = new Map(
      Array.from(outputMap, ([key, values]) => [
        key,
        Array.from(values).sort(([valueA, countA], [valueB, countB]) => {
          if (countA === 0 && countB > 0) {
            return 1;
          }
          if (countB === 0 && countA > 0) {
            return -1;
          }
          return valueA.localeCompare(valueB);
        }),
      ]),
    );
    return output;
  },
  (state: State) => [
    state.timer.selectedTags,
    ...state.currentEventIndices.map((i) => state.events[i]),
  ],
);
export const getOptions = (state: State) => state.options;
